/**
 * Client API Race Engineer — punta al backend Cloud Run.
 */
const BASE    = process.env.REACT_APP_RACE_ENGINEER_API_URL || 'https://race-engineer-api-458274493354.europe-west1.run.app';
const API_KEY = process.env.REACT_APP_RACE_ENGINEER_API_KEY || 'dev-key-locale';

const headers = { 'X-API-Key': API_KEY };

/**
 * Detect tipo file AiM dal primo blocco di byte. Evita di mandare al backend
 * file di tipo sbagliato (es. split report al posto del CSV principale, o .xrk binario).
 * Restituisce 'telemetry' | 'split_report' | 'binary' | 'unknown'.
 */
async function detectAimFileType(file) {
  if (!file) return 'unknown';
  // Leggi solo i primi 256 byte: sufficienti per identificare il tipo
  const blob = file.slice(0, 256);
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);

  // Detect binario: >5% di byte non-stampabili = quasi certo binario (es. .xrk)
  let nonPrintable = 0;
  for (const b of bytes) {
    if (b < 9 || (b > 13 && b < 32)) nonPrintable++;
  }
  if (bytes.length > 0 && nonPrintable / bytes.length > 0.05) return 'binary';

  // Decode come testo per controllare il magic della prima riga
  const text = new TextDecoder('utf-8', { fatal: false }).decode(bytes).replace(/^﻿/, '');
  const firstLine = text.split(/\r?\n/, 1)[0].trim().toLowerCase();
  if (firstLine.startsWith('"format"') && firstLine.includes('aim')) return 'telemetry';
  if (firstLine.startsWith('"segment times"') || firstLine.startsWith('"segments"')) return 'split_report';
  // Header diretto Race Studio moderno: ,"1","2",..."N"
  if (/^,?\s*"?1"?\s*,\s*"?2"?/.test(firstLine)) return 'split_report';
  return 'unknown';
}

/** Parse veloce: mappa + settori senza Claude */
export async function parseCsv({ csv, splitReport, referenceCsv, referenceSplit, track = 'auto', lap = 'best' }) {
  // Pre-check client-side: errore chiaro istantaneo se il file principale è del tipo sbagliato.
  const kind = await detectAimFileType(csv);
  if (kind === 'binary') {
    throw new Error("File binario rilevato (es. .xrk). Esporta in CSV da Race Studio prima di caricare: File → Export → CSV.");
  }
  if (kind === 'split_report') {
    throw new Error("Il file caricato è uno Split Report (solo tempi sui giri/settori), non un file di telemetria. Caricalo nel campo 'Split Report' invece che nel campo 'Telemetria CSV'.");
  }

  const form = new FormData();
  form.append('file', csv);
  if (splitReport)    form.append('split_report',    splitReport);
  if (referenceCsv)   form.append('reference_csv',   referenceCsv);
  if (referenceSplit) form.append('reference_split',  referenceSplit);
  form.append('track', track);
  form.append('lap', lap);

  let res;
  try {
    res = await fetch(`${BASE}/api/v1/parse`, { method: 'POST', headers, body: form });
  } catch (netErr) {
    // TypeError: failed to fetch arriva qui (CORS, DNS, offline, ecc.)
    throw new Error(`Errore di rete contattando l'API Race Engineer (${netErr.message}). Verifica la connessione o ricarica la pagina con Ctrl+F5.`);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail ?? 'Errore parsing');
  }
  return res.json();
}

export async function analyzeCsv({ csv, splitReport, referenceCsv, referenceSplit, track = 'auto', lap = 'best' }) {
  // Stesso pre-check di parseCsv per dare errore chiaro client-side
  const kind = await detectAimFileType(csv);
  if (kind === 'binary') {
    throw new Error("File binario rilevato (es. .xrk). Esporta in CSV da Race Studio prima di caricare: File → Export → CSV.");
  }
  if (kind === 'split_report') {
    throw new Error("Il file caricato è uno Split Report (solo tempi sui giri/settori), non un file di telemetria. Caricalo nel campo 'Split Report' invece che nel campo 'Telemetria CSV'.");
  }

  const form = new FormData();
  form.append('file', csv);
  if (splitReport)    form.append('split_report',    splitReport);
  if (referenceCsv)   form.append('reference_csv',   referenceCsv);
  if (referenceSplit) form.append('reference_split',  referenceSplit);
  form.append('track', track);
  form.append('lap', lap);

  let res;
  try {
    res = await fetch(`${BASE}/api/v1/analyze`, { method: 'POST', headers, body: form });
  } catch (netErr) {
    throw new Error(`Errore di rete contattando l'API Race Engineer (${netErr.message}). Verifica la connessione o ricarica la pagina con Ctrl+F5.`);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail ?? 'Errore analisi');
  }
  return res.json();
}

export async function recoach({ sectors, session_summary, weather, driver_feedback = [], setup = null, coaching_type = 'guida', history = null, language = 'it' }) {
  const res = await fetch(`${BASE}/api/v1/coaching`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sectors, session_summary, weather, driver_feedback, setup, coaching_type, history, language }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail ?? 'Errore ri-analisi');
  }
  return res.json();
}

export async function healthCheck() {
  const res = await fetch(`${BASE}/api/v1/health`, { headers });
  return res.ok;
}

export async function getCircuits() {
  const res = await fetch(`${BASE}/api/v1/circuits`, { headers });
  if (!res.ok) return [];
  const data = await res.json();
  return data.circuits || [];
}

export async function getCircuitById(id) {
  const res = await fetch(`${BASE}/api/v1/circuits/${id}`, { headers });
  if (!res.ok) return null;
  return res.json();
}
